import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  createSearchParams,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { REDIRECT_COOKIE } from "../cookieUtils";
import { Urls } from "../domain/urls";
import { GithubIcon } from "../icons/GithubIcon";
import { GoogleIcon } from "../icons/GoogleIcon";
import { AccountBalance } from "@mui/icons-material";
import Grid from "@mui/material/Unstable_Grid2";

import {
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Box,
  Link,
  Tooltip,
} from "@mui/material";

import { AuthTheme } from "./AuthLayout";
import { REDIRECT_URI_PARAM } from "./constants";
import { LoginFailReasons } from "./types";
import { Page } from "../components/layout/page";
import {
  StyledAuthForm,
  Group,
  HalfDivider,
  SectionCard,
  ThirdPartyButton,
  Header,
} from "./components";
import { AuthenticationContext } from "../crud/auth/context";
import { useLoginMutation } from "../crud/auth/hooks";
import { useCookie } from "../utils/hooks";
import { HTTPError } from "ky";
import { StringParam, withDefault } from "serialize-query-params";
import { useQueryParam } from "use-query-params";
import { LoadingButton } from "@mui/lab";
import { analytics } from "../analytics";
import { CardStack, CardText, CardTitle, TesimonialCard } from "./CardStack";

export const FE_USER_INFO_FORM_ENABLED = false;

type Inputs = {
  username: string;
  password: string;
};

const ReSendConfirmationEmail = () => {
  return (
    <>
      {" "}
      We need to confirm your email address.
      <br />
      Please check your email or{" "}
      <Link href={Urls.VerifyEmailReSend} color="error">
        request another confirmation email
      </Link>
      .
    </>
  );
};

export const LoginForm = (): React.ReactElement => {
  const navigate = useNavigate();
  const loginMutate = useLoginMutation();
  const [searchParams] = useSearchParams();
  const { setToken } = useContext(AuthenticationContext);
  const location = useLocation();

  const match = useMatch(`/${Urls.Login}`);
  if (!match) {
    navigate(
      {
        pathname: `/${Urls.Login}`,
        search: createSearchParams({
          [REDIRECT_URI_PARAM]: `${location.pathname}?${searchParams}`,
        }).toString(),
      },
      {
        replace: true,
      },
    );
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<Inputs>({ mode: "onChange" });

  const [requestedRedirect] = useQueryParam(
    REDIRECT_URI_PARAM,
    withDefault(StringParam, "/"),
  );
  const [redirectCookie, setRedirectCookie] = useCookie(REDIRECT_COOKIE);
  useEffect(() => {
    // Store the redirect URI in a cookie, so that we can send the user
    // there after login
    if (requestedRedirect !== redirectCookie) {
      setRedirectCookie(requestedRedirect);
    }
  }, [requestedRedirect, redirectCookie, setRedirectCookie]);
  const [loginError, setLoginError] = useState<LoginFailReasons | undefined>();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const onSubmit = ({ username, password }: Inputs) => {
    loginMutate.mutate(
      { username, password },
      {
        onSuccess: (t) => {
          setToken(t);
          analytics.track("login-success", { provider: "password" });
          navigate(requestedRedirect);
          setRedirectCookie(undefined);
        },
        onError: async (err) => {
          if (err instanceof HTTPError) {
            if (err.response?.status === 403 || err.response?.status === 429) {
              setLoginError(LoginFailReasons.LockedAccount);
            }
            const responseBody = await err.response?.json();
            // django rest auth validation error
            const errorMessage = responseBody?.non_field_errors?.[0];
            if (errorMessage === "E-mail is not verified.") {
              setLoginError(LoginFailReasons.ValidateEmail);
            } else if (
              errorMessage === "Unable to log in with provided credentials."
            ) {
              setLoginError(LoginFailReasons.InvalidCredentials);
            } else if (responseBody?.detail === "SSO required") {
              // `detail` is the arg when auth backend raises `AuthenticationFailed` exception
              setLoginError(LoginFailReasons.SsoRequired);
            } else {
              setLoginError(LoginFailReasons.Unknown);
            }
          } else {
            setLoginError(LoginFailReasons.Unknown);
          }
        },
      },
    );
  };
  const breadCrumbs = useMemo(() => [], []);
  return (
    <Page title="Login" breadCrumbs={breadCrumbs} noPadding>
      <AuthTheme>
        <Header />
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: "#101716",
            width: "100%",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: "50%",
              background:
                "radial-gradient(495.78% 97.39% at 50% 103.93%, #2F514C 0%, #041615 15.52%, #040B09 48.01%)",
              display: { xs: "none", md: "block" },
            }}
          />
          <Grid
            container
            sx={{
              maxWidth: "1450px",
              margin: "0 auto",
              minHeight: "100vh",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Grid
              xs={12}
              md={6}
              display="flex"
              flexDirection="column"
              alignItems="center"
              minHeight="100vh"
              sx={{
                paddingTop: "64px",
              }}
            >
              <SectionCard>
                <CardHeader
                  title="Login to Coiled"
                  titleTypographyProps={{
                    variant: "h4",
                  }}
                />
                <CardContent sx={{ width: "100%" }}>
                  <StyledAuthForm
                    onSubmit={handleSubmit(onSubmit)}
                    id="signin-form"
                  >
                    {loginMutate.isError && (
                      <Typography align="center" color="#FF9172">
                        {loginError === LoginFailReasons.ValidateEmail ? (
                          <ReSendConfirmationEmail />
                        ) : (
                          loginError
                        )}
                      </Typography>
                    )}
                    <Group>
                      <ThirdPartyButton
                        icon={<GoogleIcon />}
                        href={`/${Urls.GoogleInitLogin}`}
                        onClick={() =>
                          analytics.track("login-clicked", {
                            provider: "google",
                          })
                        }
                      >
                        Continue with Google
                      </ThirdPartyButton>
                      <ThirdPartyButton
                        icon={<GithubIcon />}
                        href={`/${Urls.GithubInitLogin}`}
                        onClick={() =>
                          analytics.track("login-clicked", {
                            provider: "github",
                          })
                        }
                      >
                        Continue with GitHub
                      </ThirdPartyButton>
                      <ThirdPartyButton
                        icon={<AccountBalance />}
                        href={"/workos"}
                        onClick={() =>
                          analytics.track("login-clicked", { provider: "sso" })
                        }
                      >
                        Continue with SSO
                      </ThirdPartyButton>
                    </Group>

                    <HalfDivider>OR, FIND YOUR TEAM</HalfDivider>

                    <Group>
                      <Box>
                        <TextField
                          {...register("username", {
                            required: true,
                          })}
                          error={!!errors.username}
                          helperText={
                            errors.username ? "Username is required" : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Username or email"
                          id="username"
                          // Hack to prevent Safari autofill from showing a weird yellow background color
                          sx={{
                            "& input:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 100px #121212 inset",
                              WebkitTextFillColor: "#fff",
                            },
                          }}
                        />
                      </Box>

                      <Box>
                        <TextField
                          {...register("password", { required: true })}
                          error={!!errors.password}
                          helperText={
                            errors.password ? "Password is required" : ""
                          }
                          fullWidth
                          variant="outlined"
                          label="Password"
                          type="password"
                          id="password"
                          // Hack to prevent Safari autofill from showing a weird yellow background color
                          sx={{
                            "& input:-webkit-autofill": {
                              WebkitBoxShadow: "0 0 0 100px #121212 inset",
                              WebkitTextFillColor: "#fff",
                            },
                          }}
                        />
                      </Box>

                      <Tooltip
                        title="To submit, fill out all required fields"
                        placement="bottom"
                        open={tooltipIsOpen}
                        onOpen={() => !isValid && setTooltipIsOpen(true)}
                        onClose={() => setTooltipIsOpen(false)}
                      >
                        <span>
                          <LoadingButton
                            fullWidth
                            type="submit"
                            onClick={() =>
                              analytics.track("login-clicked", {
                                provider: "password",
                              })
                            }
                            id="login-form-submit"
                            loading={loginMutate.isLoading}
                            disabled={!isValid}
                            variant={!isValid ? "outlined" : "contained"}
                            color={!isValid ? "secondary" : "primary"}
                          >
                            Login
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    </Group>

                    <Group>
                      <Typography variant="body2" color="text.secondary">
                        <Link href={Urls.ForgotPassword}>Forgot Password?</Link>
                      </Typography>
                    </Group>
                  </StyledAuthForm>
                </CardContent>
              </SectionCard>
              <Box textAlign="center" mb={2}>
                <Typography variant="body2" color="text.secondary">
                  By using Coiled, you are agreeing to our
                  <br />
                  <Link href="https://coiled.io/terms-and-conditions">
                    Terms of Service
                  </Link>
                </Typography>
              </Box>
            </Grid>
            <Grid
              xs={12}
              md={6}
              display={{ xs: "none", md: "flex" }}
              alignItems="center"
              justifyContent="center"
            >
              <Box sx={{ width: "100%", maxWidth: "725px", margin: "0 auto" }}>
                <CardStack>
                  <TesimonialCard>
                    <CardText>
                      “The speed is nice, sure, but the real benefit is taking a
                      multi-day effort and finishing it in an afternoon.{" "}
                      <span style={{ color: "white" }}>
                        Coiled changed the character of our work.
                      </span>
                      “
                    </CardText>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      alignItems="center"
                    >
                      <CardTitle>Matt Plough, Software Engineer</CardTitle>
                      <img
                        src="/static/customer-logos/kobold.png"
                        alt="KoBold Metals"
                        style={{ width: "140px" }}
                      />
                    </Box>
                  </TesimonialCard>
                  <TesimonialCard>
                    <CardText>
                      “Quite literally{" "}
                      <span style={{ color: "white" }}>
                        burst to the cloud from your laptop
                      </span>
                      , everything I&apos;ve been dreaming of since grad
                      school.“
                    </CardText>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      alignItems="center"
                    >
                      <CardTitle>Eric Ma, Data Scientist</CardTitle>
                      <img
                        src="/static/customer-logos/moderna.png"
                        alt="Moderna"
                        style={{ width: "140px" }}
                      />
                    </Box>
                  </TesimonialCard>
                  <TesimonialCard>
                    <CardText>
                      “
                      <span style={{ color: "white" }}>
                        Coiled support is amazing
                      </span>
                      . I&apos;ll run into an issue and before I have a chance
                      to mention it I have an email in my inbox. You don&apos;t
                      get this kind of support with large companies.“
                    </CardText>
                    <Box
                      display="flex"
                      flexDirection="column"
                      gap="16px"
                      alignItems="center"
                    >
                      <CardTitle>Katya Potapov, Software Engineer</CardTitle>
                      <img
                        src="/static/customer-logos/floodbase.png"
                        alt="Floodbase"
                        style={{ width: "140px" }}
                      />
                    </Box>
                  </TesimonialCard>
                </CardStack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </AuthTheme>
    </Page>
  );
};
